import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { LanguageInitializer } from "./components/LanguageInitializer";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Courses from "./pages/Courses";
import { AdminRoute } from "./components/AdminRoute";
import { InstructorRoute } from "./components/InstructorRoute";
import { AuthRoute } from "./components/AuthRoute";
import { useScrollbarVisibility } from "./hooks/useScrollbarVisibility";
import { ScrollToTop } from "./components/ScrollToTop";
import { SiteWidePromotionBanner } from "./components/SiteWidePromotionBanner";
import { PromotionPopup } from "./components/PromotionPopup";
import { MaintenanceGuard } from "./components/MaintenanceGuard";

// Lazy load non-critical routes
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CourseViewer = lazy(() => import("./pages/CourseViewer"));
const Certificate = lazy(() => import("./pages/Certificate"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const UserSubscriptionManagement = lazy(() => import("./pages/SubscriptionManagement"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Support = lazy(() => import("./pages/Support"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Gamification = lazy(() => import("./pages/Gamification"));
const LearningAnalytics = lazy(() => import("./pages/LearningAnalytics"));
const MyAnalytics = lazy(() => import("./pages/MyAnalytics"));
const MyPerformance = lazy(() => import("./pages/MyPerformance"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const Messages = lazy(() => import("./pages/Messages"));
const AiSettings = lazy(() => import("./pages/AiSettings"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Lazy load admin routes
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const QuizAnalyticsDashboard = lazy(() => import("./pages/admin/QuizAnalyticsDashboard"));
const AiQuizTools = lazy(() => import("./pages/admin/AiQuizTools"));
const UsersManagement = lazy(() => import("./pages/admin/UsersManagement"));
const CoursesManagement = lazy(() => import("./pages/admin/CoursesManagement"));
const CourseEditor = lazy(() => import("./pages/admin/CourseEditor"));
const ActivityTracking = lazy(() => import("./pages/admin/ActivityTracking"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const CommunicationsManagement = lazy(() => import("./pages/admin/CommunicationsManagement"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const InstructorsManagement = lazy(() => import("./pages/admin/InstructorsManagement"));
const AdvancedAnalytics = lazy(() => import("./pages/admin/AdvancedAnalytics"));
const FeedbackManagement = lazy(() => import("./pages/admin/FeedbackManagement"));
const PromotionsManagement = lazy(() => import("./pages/admin/PromotionsManagement"));
const DiscountsManagement = lazy(() => import("./pages/admin/DiscountsManagement"));
const SecurityDashboard = lazy(() => import("./pages/admin/SecurityDashboard"));
const SecurityAudit = lazy(() => import("./pages/admin/SecurityAudit"));
const IntegrationTesting = lazy(() => import("./pages/admin/IntegrationTesting"));
const SecurityVulnerabilityTesting = lazy(() => import("./pages/admin/SecurityVulnerabilityTesting"));
const SecurityScanner = lazy(() => import("./pages/admin/SecurityScanner"));
const SecurityCompliance = lazy(() => import("./pages/admin/SecurityCompliance"));
const UserIntelligence = lazy(() => import("./pages/admin/UserIntelligence"));
const ABTestingDashboard = lazy(() => import("./pages/admin/ABTestingDashboard"));
const StudentEnrollments = lazy(() => import("./pages/admin/StudentEnrollments"));
const SubscriptionManagement = lazy(() => import("./pages/admin/SubscriptionManagement"));
const PlatformPlans = lazy(() => import("./pages/admin/PlatformPlans"));
const PaymentOperations = lazy(() => import("./pages/admin/PaymentOperations"));
const PaymentHealthDashboard = lazy(() => import("./pages/admin/PaymentHealthDashboard"));
const AccessManagement = lazy(() => import("./pages/admin/AccessManagement"));
const CustomerSupport = lazy(() => import("./pages/admin/CustomerSupport"));
const RoleManagement = lazy(() => import("./pages/admin/RoleManagement"));
const AdminCertificatesManagement = lazy(() => import("./pages/admin/CertificatesManagement"));
const SilvaCoinsManagement = lazy(() => import("./pages/admin/SilvaCoinsManagement"));
const BadgesManagement = lazy(() => import("./pages/admin/BadgesManagement"));
const AchievementsManagement = lazy(() => import("./pages/admin/AchievementsManagement"));
const ChallengesManagement = lazy(() => import("./pages/admin/ChallengesManagement"));
const GamificationOverview = lazy(() => import("./pages/admin/GamificationOverview"));
const CategoriesManagement = lazy(() => import("./pages/admin/CategoriesManagement"));
const FeatureFlags = lazy(() => import("./pages/admin/FeatureFlags"));
const InvitationsAdmin = lazy(() => import("./pages/admin/Invitations"));
const MarketingCampaigns = lazy(() => import("./pages/admin/MarketingCampaigns"));
const BusinessGrowthDashboard = lazy(() => import("./pages/admin/BusinessGrowthDashboard"));

// Lazy load user routes
const Invitations = lazy(() => import("./pages/Invitations"));

// Lazy load public marketing pages
const PartnerCampaign = lazy(() => import("./pages/PartnerCampaign"));

// Lazy load instructor routes
const InstructorDashboard = lazy(() => import("./pages/instructor/InstructorDashboard"));
const FeedbackView = lazy(() => import("./pages/instructor/FeedbackView"));
const AnnouncementsPage = lazy(() => import("./pages/instructor/AnnouncementsPage"));
const CertificatesManagement = lazy(() => import("./pages/instructor/CertificatesManagement"));

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 2 * 60 * 1000, // 2 minutes default
        gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
        refetchOnWindowFocus: false, // Don't refetch on window focus by default
        retry: 1, // Only retry failed requests once
      },
    },
  });

const App = () => {
  useScrollbarVisibility();
  
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageInitializer>
        <ScrollToTop />
        <SiteWidePromotionBanner />
        <PromotionPopup />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="loading-spinner" /></div>}>
          <Routes>
          {/* Public routes without maintenance guard */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/maintenance" element={<Maintenance />} />
          
          {/* All other routes wrapped with MaintenanceGuard */}
          <Route path="/" element={<MaintenanceGuard><Index /></MaintenanceGuard>} />
          <Route path="/partner/:slug" element={<MaintenanceGuard><PartnerCampaign /></MaintenanceGuard>} />
          <Route path="/courses" element={<MaintenanceGuard><Courses /></MaintenanceGuard>} />
          {/* SEO-friendly slug routes (try first) */}
          <Route path="/courses/:slug" element={<MaintenanceGuard><CourseDetail /></MaintenanceGuard>} />
          {/* Legacy UUID fallback for backwards compatibility */}
          <Route path="/courses/id/:id" element={<MaintenanceGuard><CourseDetail /></MaintenanceGuard>} />
          {/* SEO-friendly slug route for course viewer */}
          <Route path="/learn/:courseSlug" element={<MaintenanceGuard><AuthRoute><CourseViewer /></AuthRoute></MaintenanceGuard>} />
          {/* Legacy UUID fallback */}
          <Route path="/learn/id/:courseId" element={<MaintenanceGuard><AuthRoute><CourseViewer /></AuthRoute></MaintenanceGuard>} />
          <Route path="/certificates/:certificateId" element={<MaintenanceGuard><AuthRoute><Certificate /></AuthRoute></MaintenanceGuard>} />
          <Route path="/pricing" element={<MaintenanceGuard><Pricing /></MaintenanceGuard>} />
          
          <Route path="/contact" element={<MaintenanceGuard><Contact /></MaintenanceGuard>} />
          <Route path="/faq" element={<MaintenanceGuard><FAQ /></MaintenanceGuard>} />
          <Route path="/about" element={<MaintenanceGuard><About /></MaintenanceGuard>} />
          <Route path="/support" element={<MaintenanceGuard><Support /></MaintenanceGuard>} />
          <Route path="/blog" element={<MaintenanceGuard><Blog /></MaintenanceGuard>} />
          <Route path="/blog/:slug" element={<MaintenanceGuard><BlogPost /></MaintenanceGuard>} />
          <Route path="/privacy-policy" element={<MaintenanceGuard><PrivacyPolicy /></MaintenanceGuard>} />
          <Route path="/terms-of-service" element={<MaintenanceGuard><TermsOfService /></MaintenanceGuard>} />
          <Route path="/cookie-policy" element={<MaintenanceGuard><CookiePolicy /></MaintenanceGuard>} />
          <Route path="/announcements" element={<MaintenanceGuard><AuthRoute><Announcements /></AuthRoute></MaintenanceGuard>} />
          
          {/* Admin Routes - Accessible during maintenance */}
          <Route path="/admin" element={<MaintenanceGuard><AdminRoute><AdminDashboard /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/users" element={<MaintenanceGuard><AdminRoute><UsersManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/courses" element={<MaintenanceGuard><AdminRoute><CoursesManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/courses/new" element={<MaintenanceGuard><AdminRoute><CourseEditor /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/courses/:courseId/edit" element={<MaintenanceGuard><AdminRoute><CourseEditor /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/categories" element={<MaintenanceGuard><AdminRoute><CategoriesManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/enrollments" element={<MaintenanceGuard><AdminRoute><StudentEnrollments /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/activity-tracking" element={<MaintenanceGuard><AdminRoute><ActivityTracking /></AdminRoute></MaintenanceGuard>} />
        <Route path="/admin/analytics" element={<MaintenanceGuard><AdminRoute><Analytics /></AdminRoute></MaintenanceGuard>} />
        <Route path="/admin/advanced-analytics" element={<MaintenanceGuard><AdminRoute><AdvancedAnalytics /></AdminRoute></MaintenanceGuard>} />
        <Route path="/admin/business-growth" element={<MaintenanceGuard><AdminRoute><BusinessGrowthDashboard /></AdminRoute></MaintenanceGuard>} />
        <Route path="/admin/quiz-analytics" element={<MaintenanceGuard><AdminRoute><QuizAnalyticsDashboard /></AdminRoute></MaintenanceGuard>} />
        <Route path="/admin/ai-quiz-tools" element={<MaintenanceGuard><AdminRoute><AiQuizTools /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/instructors" element={<MaintenanceGuard><AdminRoute><InstructorsManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/feedback" element={<MaintenanceGuard><AdminRoute><FeedbackManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/promotions" element={<MaintenanceGuard><AdminRoute><PromotionsManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/discounts" element={<MaintenanceGuard><AdminRoute><DiscountsManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/subscription-management" element={<MaintenanceGuard><AdminRoute><SubscriptionManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/platform-plans" element={<MaintenanceGuard><AdminRoute><PlatformPlans /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/payment-operations" element={<MaintenanceGuard><AdminRoute><PaymentOperations /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/payment-health" element={<MaintenanceGuard><AdminRoute><PaymentHealthDashboard /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/access-management" element={<MaintenanceGuard><AdminRoute><AccessManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/customer-support" element={<MaintenanceGuard><AdminRoute><CustomerSupport /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/role-management" element={<MaintenanceGuard><AdminRoute><RoleManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/communications" element={<MaintenanceGuard><AdminRoute><CommunicationsManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/blog" element={<MaintenanceGuard><AdminRoute><BlogManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/security" element={<MaintenanceGuard><AdminRoute><SecurityDashboard /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/security-audit" element={<MaintenanceGuard><AdminRoute><SecurityAudit /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/integration-testing" element={<MaintenanceGuard><AdminRoute><IntegrationTesting /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/security-vulnerability-testing" element={<MaintenanceGuard><AdminRoute><SecurityVulnerabilityTesting /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/security-scanner" element={<MaintenanceGuard><AdminRoute><SecurityScanner /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/security-compliance" element={<MaintenanceGuard><AdminRoute><SecurityCompliance /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/user-intelligence" element={<MaintenanceGuard><AdminRoute><UserIntelligence /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/ab-testing" element={<MaintenanceGuard><AdminRoute><ABTestingDashboard /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/certificates" element={<MaintenanceGuard><AdminRoute><AdminCertificatesManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/silva-coins" element={<MaintenanceGuard><AdminRoute><SilvaCoinsManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/gamification" element={<MaintenanceGuard><AdminRoute><GamificationOverview /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/badges" element={<MaintenanceGuard><AdminRoute><BadgesManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/achievements" element={<MaintenanceGuard><AdminRoute><AchievementsManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/challenges" element={<MaintenanceGuard><AdminRoute><ChallengesManagement /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/feature-flags" element={<MaintenanceGuard><AdminRoute><FeatureFlags /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/invitations" element={<MaintenanceGuard><AdminRoute><InvitationsAdmin /></AdminRoute></MaintenanceGuard>} />
          <Route path="/admin/marketing-campaigns" element={<MaintenanceGuard><AdminRoute><MarketingCampaigns /></AdminRoute></MaintenanceGuard>} />
          
          {/* Instructor Routes - Blocked during maintenance */}
          <Route path="/instructor/dashboard" element={<MaintenanceGuard><InstructorRoute><InstructorDashboard /></InstructorRoute></MaintenanceGuard>} />
          <Route path="/instructor/feedback" element={<MaintenanceGuard><InstructorRoute><FeedbackView /></InstructorRoute></MaintenanceGuard>} />
          <Route path="/instructor/announcements" element={<MaintenanceGuard><InstructorRoute><AnnouncementsPage /></InstructorRoute></MaintenanceGuard>} />
          <Route path="/instructor/certificates" element={<MaintenanceGuard><InstructorRoute><CertificatesManagement /></InstructorRoute></MaintenanceGuard>} />
          
          {/* Authenticated User Routes - Blocked during maintenance */}
          <Route path="/dashboard" element={<MaintenanceGuard><AuthRoute><Dashboard /></AuthRoute></MaintenanceGuard>} />
          <Route path="/gamification" element={<MaintenanceGuard><AuthRoute><Gamification /></AuthRoute></MaintenanceGuard>} />
          <Route path="/my-analytics" element={<MaintenanceGuard><AuthRoute><MyAnalytics /></AuthRoute></MaintenanceGuard>} />
          <Route path="/my-performance" element={<MaintenanceGuard><AuthRoute><MyPerformance /></AuthRoute></MaintenanceGuard>} />
          <Route path="/learning-analytics" element={<MaintenanceGuard><InstructorRoute><LearningAnalytics /></InstructorRoute></MaintenanceGuard>} />
          <Route path="/profile" element={<MaintenanceGuard><AuthRoute><Profile /></AuthRoute></MaintenanceGuard>} />
          <Route path="/profile/edit" element={<MaintenanceGuard><AuthRoute><EditProfile /></AuthRoute></MaintenanceGuard>} />
          <Route path="/settings" element={<MaintenanceGuard><AuthRoute><Settings /></AuthRoute></MaintenanceGuard>} />
          <Route path="/settings/ai" element={<MaintenanceGuard><InstructorRoute><AiSettings /></InstructorRoute></MaintenanceGuard>} />
          <Route path="/messages" element={<MaintenanceGuard><AuthRoute><Messages /></AuthRoute></MaintenanceGuard>} />
          <Route path="/payment-history" element={<MaintenanceGuard><AuthRoute><PaymentHistory /></AuthRoute></MaintenanceGuard>} />
          <Route path="/subscription-management" element={<MaintenanceGuard><AuthRoute><UserSubscriptionManagement /></AuthRoute></MaintenanceGuard>} />
          <Route path="/invitations" element={<MaintenanceGuard><AuthRoute><Invitations /></AuthRoute></MaintenanceGuard>} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<MaintenanceGuard><NotFound /></MaintenanceGuard>} />
        </Routes>
        </Suspense>
        </LanguageInitializer>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
